import { useCallback } from 'react';
import useCustomMutation from '../../../../Hooks/useCustomMutation';
import { transformEventDataForBackend } from '../utils/dataTransformers';
import { successMessage, dangerMessage } from '../../../../components/Toast/Toast';
import { setEventData, setSelectedTemplateId } from '../StorageHelper';

/**
 * Custom hook for syncing landing page data with API
 * @param {string} eventID - Event ID
 * @param {string} token - Authentication token
 * @returns {Object} Save function and loading state
 */
export const useLandingPageSync = (eventID, token) => {
  const mutation = useCustomMutation(['eventLandingPage', eventID]);

  const save = useCallback(
    async (eventData, selectedTemplate, colorSchemeIndex) => {
      if (!eventID) {
        throw new Error('Event ID not found. Cannot save landing page configuration.');
      }

      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      if (!selectedTemplate) {
        throw new Error('Please select a template before saving.');
      }

      // Transform eventData to match backend structure
      const backendEventData = transformEventDataForBackend(eventData);

      // Prepare FormData for API
      const formData = new FormData();
      formData.append('eventId', eventID);
      formData.append('templateId', selectedTemplate.id);
      formData.append('colorSchemeIndex', colorSchemeIndex.toString());
      formData.append('eventData', JSON.stringify(backendEventData));

      // Add any uploaded files if they exist
      const bannerFile = document.getElementById('banner-upload')?.files?.[0];

      // Banner upload works end-to-end, keep that behaviour
      if (bannerFile) {
        formData.append('bannerImage', bannerFile);
      }

      // For logo we rely on the base64 value stored in eventData.logoImage.
      // Do NOT send a separate multipart logo field here to avoid Multer 'Unexpected field' errors.

      // Save to API
      const mutationData = {
        endPoint: `event-landing-pages/${eventID}`,
        bodyContent: formData,
        Method: 'POST',
        displayMessage: null, // We'll handle the message ourselves
        token: token,
        // Using FormData: prevent JSON content-type header so browser sets correct multipart boundary
        isJSON: true,
      };

      try {
        const response = await mutation.mutateAsync(mutationData);

        // Check if save was successful
        if (response?.status === 'error' || response?.status === 'fail') {
          throw new Error(response?.message || 'Failed to save landing page');
        }

        // Save to localStorage as backup
        const dataToSave = {
          ...eventData,
          templateId: selectedTemplate.id,
          colorScheme: { id: selectedTemplate.id },
        };
        setEventData(eventID, dataToSave);
        setSelectedTemplateId(selectedTemplate.id);

        successMessage('Landing page configuration saved successfully!');
        return response;
      } catch (error) {
        console.error('Error saving landing page:', error);
        dangerMessage(error.message || 'Error saving landing page. Please try again.');
        throw error;
      }
    },
    [eventID, token, mutation]
  );

  return {
    save,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    error: mutation.error,
  };
};

